/**
 * @file The one place a `form_assigned_notification` payload is built.
 * @module utils/forms/formAssignedNotification
 *
 */

import { Patient } from "../../types/healthcare/PatientTypes";
import type { SendCommunicationRequest } from "../../types/communications/CommunicationTypes";
import { buildPatientFormLink } from "./patientFormLink";
import type { UserUuid } from "../../types/identity";

/** Must match `NOTIFICATION_CATEGORIES` / the Template Builder's category list byte for byte. */
export const FORM_ASSIGNED_CATEGORY = "form_assigned_notification";

/**
 * Which contact detail the notification service should use.
 */
export type FormLinkChannel = "email" | "sms" | "both";

export interface FormAssignedNotificationInput {
	assignedFormId: number | string;
	accessToken?: string | null;
	patient?: Partial<Patient> | null;
}

/**
 * The `data` bag. ⚠️ `form_link` is spread LAST so a patient record that happens to carry a
 * `form_link` column can never shadow the link we just built.
 */
const formAssignedData = ({ assignedFormId, accessToken, patient }: FormAssignedNotificationInput) => ({
	...(patient ?? {}),
	form_link: buildPatientFormLink(assignedFormId, accessToken),
});

/**
 * The email/SMS-addressed payload (`POST /api/notifications/send`).
 *
 * Pass the result straight to `sendNotificationWithCompanyConfig`.
 */
export const buildFormAssignedNotification = (
	input: FormAssignedNotificationInput & { companyId: number; channel?: FormLinkChannel },
): Omit<SendCommunicationRequest, "user_id"> & { company_id: number } => {

	return {
		category: FORM_ASSIGNED_CATEGORY,
		recipient_user_id: input.patient?.id ?? "",
		// Omitted for "both" so the notification service falls back to the user's preferences.
		...(input.channel && input.channel !== "both" ? { channels: [input.channel] } : {}),
		data: formAssignedData(input),
		company_id: input.companyId,
	};
};

/**
 * The user-id-addressed payload (`POST /api/communications/send`).
 *
 * Pass the result straight to `sendCommunication`. `company_id` is optional here — `FormsTab`
 * builds this before `fetchClinicCompany()` has necessarily resolved, and the notification
 * service falls back to its own config rather than rejecting.
 */
export const buildFormAssignedCommunication = (
	input: FormAssignedNotificationInput & {
		recipientUserId: UserUuid;
		companyId?: number;
		userId?: UserUuid;
	},
): SendCommunicationRequest => ({
	recipient_user_id: input.recipientUserId,
	category: FORM_ASSIGNED_CATEGORY,
	company_id: input.companyId,
	user_id: input.userId,
	data: formAssignedData(input),
});
